/**
 * ProjectDashboard — Enterprise-grade mission control for a single project.
 * Sections: Header · KPIs · Sprint+Modules+Risks · Issues · Bugs · Milestones · Team
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { pm, hrms, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  CircleDot, CheckCircle2, AlertTriangle, Bug,
  Users, Clock, Flag, Layers, RefreshCw, TrendingUp, TrendingDown,
  Target, DollarSign, Calendar, ShieldAlert,
  BarChart2, ArrowUpRight, Flame, AlertCircle, CheckCheck,
} from 'lucide-react';

/* ══════════════════════════ DESIGN TOKENS ══════════════════════════════════ */
const T = {
  bg:       '#F8FAFC',
  surface:  '#FFFFFF',
  border:   '#E2E8F0',
  borderSm: '#F1F5F9',
  text:     '#0F172A',
  textSub:  '#475569',
  textMute: '#94A3B8',
  radius:   12,
  radiusSm: 8,
  shadow:   '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  shadowMd: '0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
};

const PALETTE = {
  blue:   { t:'#2563EB', bg:'#EFF6FF', b:'#BFDBFE' },
  green:  { t:'#16A34A', bg:'#F0FDF4', b:'#BBF7D0' },
  red:    { t:'#DC2626', bg:'#FEF2F2', b:'#FECACA' },
  amber:  { t:'#D97706', bg:'#FFFBEB', b:'#FDE68A' },
  purple: { t:'#7C3AED', bg:'#F5F3FF', b:'#DDD6FE' },
  teal:   { t:'#0D9488', bg:'#F0FDFA', b:'#99F6E4' },
  rose:   { t:'#E11D48', bg:'#FFF1F2', b:'#FECDD3' },
  slate:  { t:'#475569', bg:'#F8FAFC', b:'#E2E8F0' },
};

const HEALTH = {
  healthy:  { label:'Healthy',  icon: CheckCircle2,   color:'#16A34A', bg:'#F0FDF4', border:'#BBF7D0', grad:'linear-gradient(135deg,#F0FDF4,#DCFCE7)' },
  at_risk:  { label:'At Risk',  icon: AlertTriangle,  color:'#D97706', bg:'#FFFBEB', border:'#FDE68A', grad:'linear-gradient(135deg,#FFFBEB,#FEF3C7)' },
  delayed:  { label:'Delayed',  icon: Clock,          color:'#DC2626', bg:'#FEF2F2', border:'#FECACA', grad:'linear-gradient(135deg,#FEF2F2,#FEE2E2)' },
  critical: { label:'Critical', icon: Flame,          color:'#E11D48', bg:'#FFF1F2', border:'#FECDD3', grad:'linear-gradient(135deg,#FFF1F2,#FFE4E6)' },
};

const MOD_STATUS = {
  backlog:     { t:'#6B7280', bg:'#F3F4F6', label:'Backlog'      },
  planned:     { t:'#2563EB', bg:'#DBEAFE', label:'Planned'      },
  in_progress: { t:'#D97706', bg:'#FEF3C7', label:'In Progress'  },
  paused:      { t:'#7C3AED', bg:'#EDE9FE', label:'Paused'       },
  completed:   { t:'#16A34A', bg:'#DCFCE7', label:'Completed'    },
  cancelled:   { t:'#DC2626', bg:'#FEE2E2', label:'Cancelled'    },
};

const PRI_COLOR = { urgent:'#DC2626', high:'#D97706', medium:'#2563EB', low:'#16A34A', none:'#94A3B8' };
const SEV_PAL   = { critical:PALETTE.red, major:PALETTE.amber, minor:PALETTE.blue, trivial:PALETTE.slate };

const SPRINT_STATUS = {
  draft:     { t:'#6B7280', bg:'#F3F4F6', label:'Draft'       },
  started:   { t:'#D97706', bg:'#FEF3C7', label:'In Progress' },
  completed: { t:'#16A34A', bg:'#DCFCE7', label:'Completed'   },
};

/* ══════════════════════════ MICRO HELPERS ══════════════════════════════════ */
const fmt1    = n  => Number(n || 0).toFixed(1);
const fmtDate = d  => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtShort= d  => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '—';
const pct     = (a,b) => b ? Math.round((a/b)*100) : 0;

const ACCENTS = ['#3B82F6','#8B5CF6','#14B8A6','#F59E0B','#EF4444','#EC4899','#22C55E','#F97316'];
const avatarColor = (n='') => ACCENTS[(n.charCodeAt(0)||0) % ACCENTS.length];
const initials    = (n='') => n.trim().split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() || '?';

function computeHealth(stats, bugs, milestones, sprints) {
  const total    = stats?.total_issues || 0;
  const overdue  = stats?.overdue_issues || 0;
  const odRate   = total ? overdue / total : 0;
  const critBugs = bugs?.by_severity?.find(s => s.name === 'critical' && s.count > 0);
  const odMs     = milestones?.some(m => m.is_overdue && m.status !== 'completed' && m.status !== 'cancelled');
  const activeSp = sprints?.find(s => s.status === 'started');
  const spLagging= activeSp && activeSp.completion_pct < 30;
  if (odRate > 0.25 || (bugs?.open > 3 && critBugs) || spLagging) return 'critical';
  if (odRate > 0.12 || odMs)                                       return 'delayed';
  if (odRate > 0.04 || critBugs)                                   return 'at_risk';
  return 'healthy';
}

/* ══════════════════════════ BASE COMPONENTS ════════════════════════════════ */
function Card({ children, style, hover }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => hover && setHov(true)}
      onMouseLeave={() => hover && setHov(false)}
      style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: T.radius, padding: '1.25rem 1.375rem',
        boxShadow: hov ? T.shadowMd : T.shadow,
        transition: 'box-shadow 0.2s, transform 0.2s',
        transform: hov ? 'translateY(-1px)' : 'none',
        ...style,
      }}>
      {children}
    </div>
  );
}

function SecHead({ icon: Icon, label, right }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
        <Icon size={13} color={T.textMute} strokeWidth={2.5} />
        <span style={{ fontSize:'0.68rem', fontWeight:700, color:T.textMute,
                        textTransform:'uppercase', letterSpacing:'0.08em' }}>
          {label}
        </span>
      </div>
      {right && <div style={{ fontSize:'0.72rem', color:T.textMute }}>{right}</div>}
    </div>
  );
}

function Pill({ label, palette, size = 'sm' }) {
  const p = palette || PALETTE.slate;
  const fs = size === 'xs' ? '0.62rem' : '0.67rem';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: fs, fontWeight: 700, color: p.t,
      background: p.bg, border: `1px solid ${p.b}`,
      padding: size === 'xs' ? '1px 6px' : '2px 7px',
      borderRadius: 5, letterSpacing: '0.01em', whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function ProgressBar({ value, max, color = PALETTE.blue.t, height = 5 }) {
  const w = Math.min(pct(value, max || 1), 100);
  return (
    <div style={{ height, background: T.borderSm, borderRadius: 9999, overflow: 'hidden' }}>
      <div style={{
        width: `${w}%`, height: '100%', borderRadius: 9999,
        background: color, transition: 'width 0.6s cubic-bezier(.4,0,.2,1)',
      }} />
    </div>
  );
}

function Skeleton({ w = '100%', h = 16, r = 6 }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: 'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
    }} />
  );
}

function Avatar({ name, photo, size = 36 }) {
  const [err, setErr] = useState(false);
  const ac = avatarColor(name);
  const url = getFileUrl(photo);
  if (url && !err) return (
    <img src={url} alt={name} onError={() => setErr(true)}
      style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:`2px solid ${ac}30` }} />
  );
  return (
    <div style={{
      width:size, height:size, borderRadius:'50%', background:`${ac}18`, color:ac,
      fontWeight:800, fontSize: size > 40 ? '0.85rem' : '0.68rem', flexShrink:0,
      display:'flex', alignItems:'center', justifyContent:'center', border:`2px solid ${ac}30`,
    }}>{initials(name)}</div>
  );
}

/* ── Simple donut chart ── */
function Donut({ value, max, color, size = 52, stroke = 7 }) {
  const r   = (size - stroke) / 2;
  const c   = 2 * Math.PI * r;
  const pct = max ? Math.min(value / max, 1) : 0;
  return (
    <svg width={size} height={size} style={{ transform:'rotate(-90deg)', flexShrink:0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.borderSm} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
        strokeLinecap="round" style={{ transition:'stroke-dashoffset 0.7s ease' }} />
    </svg>
  );
}

/* ══════════════════════════ KPI CARD ═══════════════════════════════════════ */
function KpiCard({ icon: Icon, label, value, sub, pal, trend }) {
  const p = pal || PALETTE.blue;
  return (
    <Card hover style={{ padding:'1rem 1.25rem' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div style={{ width:36, height:36, borderRadius:9, background:p.bg,
                      border:`1px solid ${p.b}`, display:'flex', alignItems:'center',
                      justifyContent:'center', flexShrink:0 }}>
          <Icon size={16} color={p.t} strokeWidth={2.5} />
        </div>
        {trend !== undefined && (
          <span style={{ fontSize:'0.65rem', fontWeight:700,
                         color: trend >= 0 ? PALETTE.green.t : PALETTE.red.t,
                         display:'flex', alignItems:'center', gap:2 }}>
            {trend >= 0 ? <ArrowUpRight size={10}/> : <TrendingDown size={10}/>}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div style={{ marginTop:'0.625rem' }}>
        <div style={{ fontSize:'1.5rem', fontWeight:800, color:T.text, lineHeight:1.1 }}>{value}</div>
        <div style={{ fontSize:'0.73rem', fontWeight:600, color:T.textSub, marginTop:3 }}>{label}</div>
        {sub && <div style={{ fontSize:'0.64rem', color:T.textMute, marginTop:2 }}>{sub}</div>}
      </div>
    </Card>
  );
}

/* ══════════════════════════ MAIN COMPONENT ═════════════════════════════════ */
const FINANCIALS_ROLES = ['project_manager','cfo','cto','ceo','super_admin'];

export default function ProjectDashboard({ project, stats }) {
  const { user } = useAuth();
  const canSeeFinancials = FINANCIALS_ROLES.includes(user?.role) ||
    (user?.emp_id != null && project?.manager_id != null &&
     Number(user.emp_id) === Number(project.manager_id));

  const [bugs,       setBugs]       = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [wh,         setWh]         = useState(null);
  const [members,    setMembers]    = useState([]);
  const [sprints,    setSprints]    = useState([]);
  const [modules,    setModules]    = useState([]);
  const [issues,     setIssues]     = useState([]);
  const [loading,    setLoading]    = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      pm.getBugAnalytics(project.id),
      pm.getMilestones(project.id),
      hrms.getPMTimesheet({ period:'monthly', project_id:project.id }),
      pm.getMembers(project.id),
      pm.getSprints(project.id),
      pm.getModules(project.id),
      pm.getIssues(project.id, { limit:100 }),
    ]).then(([b,m,w,mem,sp,mod,iss]) => {
      setBugs(b.data);
      setMilestones(m.data || []);
      setWh(w.data);
      setMembers(mem.data || []);
      setSprints(sp.data || []);
      setModules(mod.data || []);
      setIssues(iss.data || []);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, [project.id]);

  useEffect(()=>{ load(); },[load]);

  /* ── Derived analytics ── */
  const activeSprint = sprints.find(s => s.status === 'started') || sprints[0] || null;
  const health       = useMemo(() => computeHealth(stats, bugs, milestones, sprints), [stats, bugs, milestones, sprints]);
  const H            = HEALTH[health];

  const overdueIssues = issues.filter(i => i.due_date && new Date(i.due_date) < new Date() && !i.completed_at);
  const stateMax  = stats?.state_distribution?.length ? Math.max(...stats.state_distribution.map(s=>s.count),1) : 1;
  const priMax    = stats?.priority_distribution ? Math.max(...Object.values(stats.priority_distribution),1) : 1;
  const sevMax    = bugs?.by_severity?.length ? Math.max(...bugs.by_severity.map(s=>s.count),1) : 1;

  const msOpen = milestones.filter(m=>m.status!=='completed'&&m.status!=='cancelled');
  const msOverdue = milestones.filter(m=>m.is_overdue&&m.status!=='completed');

  /* ── Risk items ── */
  const risks = [
    ...msOverdue.map(m=>({ level:'high',   text:`Milestone "${m.name}" is overdue`, icon:Flag })),
    ...(overdueIssues.length ? [{ level:'medium', text:`${overdueIssues.length} issue${overdueIssues.length>1?'s':''} past due date`, icon:AlertCircle }] : []),
    ...(bugs?.by_severity?.find(s=>s.name==='critical'&&s.count>0) ? [{ level:'critical', text:`${bugs.by_severity.find(s=>s.name==='critical').count} critical bug${bugs.by_severity.find(s=>s.name==='critical').count>1?'s':''} open`, icon:Bug }] : []),
    ...(activeSprint&&activeSprint.completion_pct<30 ? [{ level:'medium', text:`Sprint "${activeSprint.name}" behind schedule`, icon:RefreshCw }] : []),
    ...(wh?.by_employee?.filter(e=>e.hours > 160).map(e=>({ level:'low', text:`${e.name} may be overloaded (${fmt1(e.hours)}h)`, icon:Users })) || []),
  ].slice(0,6);

  const RISK_PAL = { critical:PALETTE.red, high:PALETTE.rose, medium:PALETTE.amber, low:PALETTE.blue };

  /* ══════════════════════════ RENDER ══════════════════════════════════════ */
  return (
    <div style={{ padding:'1.5rem 1.75rem', display:'flex', flexDirection:'column', gap:'1.375rem',
                  background:T.bg, minHeight:'100%', fontFamily:'inherit' }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
      `}</style>

      {/* ═══════════ HEALTH BANNER ═══════════ */}
      <div style={{
        background: H.grad, border: `1px solid ${H.border}`,
        borderRadius: T.radius, padding:'0.875rem 1.375rem',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        flexWrap:'wrap', gap:'0.75rem',
        backdropFilter:'blur(8px)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
          <H.icon size={18} color={H.color} strokeWidth={2.5} />
          <div>
            <span style={{ fontSize:'0.82rem', fontWeight:800, color:H.color, letterSpacing:'0.01em' }}>
              {H.label}
            </span>
            <span style={{ fontSize:'0.78rem', color:T.textSub, marginLeft:'0.5rem' }}>
              · {project.name}
              {project.code && <span style={{ fontFamily:'monospace', color:T.textMute, marginLeft:6 }}>{project.code}</span>}
            </span>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'1.25rem', flexWrap:'wrap' }}>
          {[
            { icon:Calendar,   val: fmtShort(project.start_date),       label:'Start'    },
            { icon:Calendar,   val: fmtShort(project.end_date),         label:'Deadline' },
            { icon:TrendingUp, val: `${stats?.completion_pct ?? 0}%`,  label:'Done'     },
          ].map(f => (
            <div key={f.label} style={{ display:'flex', alignItems:'center', gap:'0.3rem' }}>
              <f.icon size={12} color={T.textMute} strokeWidth={2} />
              <span style={{ fontSize:'0.72rem', color:T.textSub }}>
                <span style={{ fontWeight:700, color:T.text }}>{f.val}</span>
                {' '}{f.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════ KPI ROW ═══════════ */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))', gap:'0.875rem' }}>
        <KpiCard icon={CircleDot}    label="Total Issues"     value={stats?.total_issues??0}      pal={PALETTE.blue}   />
        <KpiCard icon={CheckCircle2} label="Completed"        value={stats?.completed_issues??0}   sub={`${stats?.completion_pct??0}% done`} pal={PALETTE.green} />
        <KpiCard icon={AlertTriangle}label="Overdue"          value={stats?.overdue_issues??0}     pal={(stats?.overdue_issues??0)>0?PALETTE.red:PALETTE.green} />
        <KpiCard icon={Bug}          label="Open Bugs"        value={bugs?.open??0}                sub={bugs?`${bugs.total} total`:''}  pal={PALETTE.amber} />
        <KpiCard icon={Layers}       label="Modules"          value={modules.length}               sub={`${modules.filter(m=>m.status==='in_progress').length} active`} pal={PALETTE.purple} />
        <KpiCard icon={Flag}         label="Milestones"       value={milestones.length}            sub={`${milestones.filter(m=>m.status==='completed').length} done`} pal={PALETTE.teal} />
        <KpiCard icon={Users}        label="Team Size"        value={members.length}               pal={PALETTE.slate}  />
        <KpiCard icon={Clock}        label="Hours (Month)"    value={fmt1(wh?.total_hours)}        sub={`${wh?.total_entries??0} entries`} pal={PALETTE.teal} />
      </div>

      {/* ═══════════ ROW: SPRINT · MODULES · RISKS ═══════════ */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1.4fr 1fr', gap:'1rem' }}>

        {/* Active Sprint */}
        <Card>
          <SecHead icon={RefreshCw} label="Active Sprint" />
          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              <Skeleton h={14} w="70%" /><Skeleton h={6} /><Skeleton h={12} w="50%" />
            </div>
          ) : activeSprint ? (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.625rem' }}>
                <span style={{ fontSize:'0.88rem', fontWeight:700, color:T.text }}>{activeSprint.name}</span>
                <Pill label={SPRINT_STATUS[activeSprint.status]?.label||activeSprint.status}
                  palette={SPRINT_STATUS[activeSprint.status] ? { t:SPRINT_STATUS[activeSprint.status].t, bg:SPRINT_STATUS[activeSprint.status].bg, b:SPRINT_STATUS[activeSprint.status].bg } : PALETTE.slate} />
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.5rem' }}>
                <Donut value={activeSprint.completed_issues} max={activeSprint.total_issues||1}
                  color={PALETTE.blue.t} size={48} stroke={6} />
                <div>
                  <div style={{ fontSize:'1.25rem', fontWeight:800, color:T.text }}>{activeSprint.completion_pct}%</div>
                  <div style={{ fontSize:'0.7rem', color:T.textMute }}>{activeSprint.completed_issues}/{activeSprint.total_issues} issues</div>
                </div>
              </div>
              <ProgressBar value={activeSprint.completion_pct} max={100} color={PALETTE.blue.t} height={5} />
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:'0.5rem' }}>
                <span style={{ fontSize:'0.67rem', color:T.textMute }}>
                  {activeSprint.start_date ? fmtShort(activeSprint.start_date) : '—'}
                </span>
                <span style={{ fontSize:'0.67rem', color:T.textMute }}>
                  {activeSprint.end_date ? fmtShort(activeSprint.end_date) : '—'}
                </span>
              </div>
              {sprints.length > 1 && (
                <div style={{ marginTop:'0.75rem', paddingTop:'0.625rem', borderTop:`1px solid ${T.borderSm}`,
                              fontSize:'0.67rem', color:T.textMute }}>
                  {sprints.filter(s=>s.status==='completed').length} of {sprints.length} sprints completed
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign:'center', padding:'1.5rem 0', color:T.textMute }}>
              <RefreshCw size={24} color={T.borderSm} strokeWidth={1.5} />
              <p style={{ fontSize:'0.78rem', margin:'0.5rem 0 0', color:T.textMute }}>No active sprint</p>
            </div>
          )}
        </Card>

        {/* Modules */}
        <Card>
          <SecHead icon={Layers} label="Module Status" right={`${modules.length} total`} />
          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {[1,2,3].map(i=><Skeleton key={i} h={32} />)}
            </div>
          ) : modules.length === 0 ? (
            <div style={{ textAlign:'center', padding:'1.5rem 0', color:T.textMute }}>
              <Layers size={24} color={T.borderSm} strokeWidth={1.5} />
              <p style={{ fontSize:'0.78rem', margin:'0.5rem 0 0' }}>No modules yet</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', maxHeight:220, overflowY:'auto' }}>
              {modules.map(m => {
                const sc = MOD_STATUS[m.status] || MOD_STATUS.backlog;
                const isLate = m.target_date && new Date(m.target_date)<new Date() && m.status!=='completed';
                return (
                  <div key={m.id} style={{ display:'flex', alignItems:'center', gap:'0.75rem',
                                           padding:'0.5rem 0.625rem', borderRadius:T.radiusSm,
                                           border:`1px solid ${T.borderSm}`, background:T.bg }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.375rem', marginBottom:'0.25rem' }}>
                        <span style={{ fontSize:'0.78rem', fontWeight:600, color:T.text,
                                       overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {m.name}
                        </span>
                        {isLate && <AlertTriangle size={11} color={PALETTE.amber.t} strokeWidth={2.5} />}
                      </div>
                      <ProgressBar value={m.completion_pct} max={100} color={sc.t} height={4} />
                      <div style={{ display:'flex', justifyContent:'space-between', marginTop:'0.2rem' }}>
                        <span style={{ fontSize:'0.63rem', color:T.textMute }}>
                          {m.lead_name || '—'}
                        </span>
                        <span style={{ fontSize:'0.63rem', color:T.textMute }}>
                          {m.completed_issues}/{m.total_issues}
                        </span>
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                      <span style={{ fontSize:'0.68rem', fontWeight:800, color:sc.t }}>{m.completion_pct}%</span>
                      <Pill label={sc.label} palette={{ t:sc.t, bg:sc.bg, b:sc.bg }} size="xs" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Risk & Alerts */}
        <Card>
          <SecHead icon={ShieldAlert} label="Risks & Alerts" right={risks.length ? `${risks.length} items` : null} />
          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {[1,2,3].map(i=><Skeleton key={i} h={36} />)}
            </div>
          ) : risks.length === 0 ? (
            <div style={{ textAlign:'center', padding:'1.75rem 0' }}>
              <CheckCheck size={28} color={PALETTE.green.t} strokeWidth={1.75} />
              <p style={{ fontSize:'0.78rem', color:PALETTE.green.t, fontWeight:600, margin:'0.5rem 0 0' }}>
                No risks detected
              </p>
              <p style={{ fontSize:'0.68rem', color:T.textMute, margin:'0.25rem 0 0' }}>
                Project is on track
              </p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {risks.map((r, i) => {
                const rp = RISK_PAL[r.level] || PALETTE.slate;
                return (
                  <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'0.5rem',
                                        padding:'0.5rem 0.625rem', borderRadius:T.radiusSm,
                                        background:rp.bg, border:`1px solid ${rp.b}` }}>
                    <r.icon size={13} color={rp.t} strokeWidth={2.5} style={{ flexShrink:0, marginTop:1 }} />
                    <div>
                      <span style={{ fontSize:'0.73rem', color:rp.t, fontWeight:600, lineHeight:1.3 }}>
                        {r.text}
                      </span>
                      <div style={{ fontSize:'0.62rem', color:rp.t, opacity:0.7, marginTop:1,
                                    textTransform:'uppercase', letterSpacing:'0.05em', fontWeight:700 }}>
                        {r.level}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ═══════════ ROW: ISSUE INTELLIGENCE ═══════════ */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>

        {/* State distribution */}
        <Card>
          <SecHead icon={BarChart2} label="Issue Analytics" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.25rem' }}>
            <div>
              <div style={{ fontSize:'0.67rem', fontWeight:700, color:T.textMute,
                            textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'0.625rem' }}>
                By State
              </div>
              {stats?.state_distribution?.length ? (
                stats.state_distribution.map(s => (
                  <div key={s.state} style={{ marginBottom:'0.375rem' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.2rem' }}>
                      <span style={{ fontSize:'0.72rem', color:T.textSub }}>{s.state}</span>
                      <span style={{ fontSize:'0.72rem', fontWeight:700, color:T.text }}>{s.count}</span>
                    </div>
                    <ProgressBar value={s.count} max={stateMax} color={s.color||PALETTE.slate.t} height={5} />
                  </div>
                ))
              ) : <p style={{ fontSize:'0.75rem', color:T.textMute, margin:0 }}>No issues yet</p>}
            </div>
            <div>
              <div style={{ fontSize:'0.67rem', fontWeight:700, color:T.textMute,
                            textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'0.625rem' }}>
                By Priority
              </div>
              {stats?.priority_distribution && Object.keys(stats.priority_distribution).length ? (
                ['urgent','high','medium','low','none']
                  .filter(k => stats.priority_distribution[k])
                  .map(k => (
                    <div key={k} style={{ marginBottom:'0.375rem' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.2rem' }}>
                        <span style={{ fontSize:'0.72rem', color:T.textSub, textTransform:'capitalize' }}>{k}</span>
                        <span style={{ fontSize:'0.72rem', fontWeight:700, color:T.text }}>{stats.priority_distribution[k]}</span>
                      </div>
                      <ProgressBar value={stats.priority_distribution[k]} max={priMax} color={PRI_COLOR[k]} height={5} />
                    </div>
                  ))
              ) : <p style={{ fontSize:'0.75rem', color:T.textMute, margin:0 }}>No data</p>}
            </div>
          </div>
        </Card>

        {/* Overdue issues */}
        <Card>
          <SecHead icon={AlertTriangle} label="Overdue Issues"
            right={overdueIssues.length ? <Pill label={`${overdueIssues.length} overdue`} palette={PALETTE.red} size="xs"/> : null} />
          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {[1,2,3,4].map(i=><Skeleton key={i} h={28}/>)}
            </div>
          ) : overdueIssues.length === 0 ? (
            <div style={{ textAlign:'center', padding:'1.5rem 0' }}>
              <CheckCircle2 size={26} color={PALETTE.green.t} strokeWidth={1.75} />
              <p style={{ fontSize:'0.78rem', color:PALETTE.green.t, fontWeight:600, margin:'0.5rem 0 0' }}>
                All issues on schedule
              </p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem', maxHeight:220, overflowY:'auto' }}>
              {overdueIssues.slice(0,10).map(i => {
                const daysLate = Math.round((new Date()-new Date(i.due_date))/(1000*60*60*24));
                return (
                  <div key={i.id} style={{ display:'flex', alignItems:'center', gap:'0.5rem',
                                           padding:'0.45rem 0.625rem', borderRadius:T.radiusSm,
                                           background:T.bg, border:`1px solid ${T.borderSm}` }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', flexShrink:0,
                                  background:PRI_COLOR[i.priority]||T.textMute }} />
                    <span style={{ flex:1, fontSize:'0.75rem', color:T.text, fontWeight:500,
                                   overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {i.code && <span style={{ fontFamily:'monospace', fontSize:'0.68rem', color:T.textMute, marginRight:4 }}>{i.code}</span>}
                      {i.title}
                    </span>
                    <span style={{ fontSize:'0.67rem', color:PALETTE.red.t, fontWeight:700, flexShrink:0 }}>
                      {daysLate}d late
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ═══════════ ROW: BUGS & MILESTONES ═══════════ */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>

        {/* Bug analytics */}
        <Card>
          <SecHead icon={Bug} label="Bug & QA Analytics" />

          {/* Bug KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.5rem', marginBottom:'1rem' }}>
            {[
              { l:'Open',   v:bugs?.open??0,                      p:PALETTE.red    },
              { l:'Closed', v:bugs?.closed??0,                    p:PALETTE.green  },
              { l:'SLA',    v:`${bugs?.sla_compliance??100}%`,    p:PALETTE.blue   },
              { l:'MTTR',   v:`${bugs?.mttr_hours??0}h`,          p:PALETTE.purple },
            ].map(b=>(
              <div key={b.l} style={{ background:b.p.bg, border:`1px solid ${b.p.b}`,
                                      borderRadius:9, padding:'0.5rem 0.375rem', textAlign:'center' }}>
                <div style={{ fontSize:'0.95rem', fontWeight:800, color:b.p.t, lineHeight:1 }}>{b.v}</div>
                <div style={{ fontSize:'0.62rem', fontWeight:700, color:b.p.t, opacity:0.75, marginTop:2 }}>{b.l}</div>
              </div>
            ))}
          </div>

          {/* By severity */}
          <div style={{ fontSize:'0.67rem', fontWeight:700, color:T.textMute,
                        textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'0.5rem' }}>
            By Severity
          </div>
          {bugs?.by_severity?.length ? (
            bugs.by_severity.map(s => {
              const sp = SEV_PAL[s.name]||PALETTE.slate;
              return (
                <div key={s.name} style={{ marginBottom:'0.375rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.2rem' }}>
                    <span style={{ fontSize:'0.72rem', color:T.textSub, textTransform:'capitalize' }}>{s.name}</span>
                    <span style={{ fontSize:'0.72rem', fontWeight:700, color:T.text }}>{s.count}</span>
                  </div>
                  <ProgressBar value={s.count} max={sevMax} color={sp.t} height={5}/>
                </div>
              );
            })
          ) : <p style={{ fontSize:'0.75rem', color:T.textMute, margin:0 }}>No bugs reported</p>}

          {/* Aging */}
          {bugs?.aging?.length>0 && (
            <>
              <div style={{ fontSize:'0.67rem', fontWeight:700, color:T.textMute,
                            textTransform:'uppercase', letterSpacing:'0.07em',
                            margin:'0.875rem 0 0.5rem' }}>
                Oldest Open Bugs
              </div>
              {bugs.aging.slice(0,4).map(b=>{
                const sp = SEV_PAL[b.severity]||PALETTE.slate;
                return (
                  <div key={b.id} style={{ display:'flex', alignItems:'center', gap:'0.5rem',
                                           padding:'0.375rem 0', borderBottom:`1px solid ${T.borderSm}` }}>
                    <Pill label={b.severity} palette={sp} size="xs"/>
                    <span style={{ flex:1, fontSize:'0.72rem', color:T.textSub,
                                   overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {b.title}
                    </span>
                    <span style={{ fontSize:'0.67rem', color:PALETTE.amber.t, fontWeight:700, flexShrink:0 }}>
                      {b.days}d
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </Card>

        {/* Milestones */}
        <Card>
          <SecHead icon={Target} label="Milestones & Timeline"
            right={msOpen.length ? `${msOpen.length} open` : null} />
          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {[1,2,3].map(i=><Skeleton key={i} h={68}/>)}
            </div>
          ) : milestones.length===0 ? (
            <div style={{ textAlign:'center', padding:'2rem 0', color:T.textMute }}>
              <Target size={26} color={T.border} strokeWidth={1.5}/>
              <p style={{ fontSize:'0.78rem', margin:'0.5rem 0 0' }}>No milestones defined</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', maxHeight:320, overflowY:'auto' }}>
              {milestones.map(m => {
                const ms = { open:PALETTE.blue, in_progress:PALETTE.amber, completed:PALETTE.green, cancelled:PALETTE.slate };
                const mp = ms[m.status]||PALETTE.slate;
                const msLabel = { open:'Open', in_progress:'In Progress', completed:'Completed', cancelled:'Cancelled' };
                return (
                  <div key={m.id} style={{
                    border:`1px solid ${mp.b}`, borderLeft:`3px solid ${mp.t}`,
                    borderRadius:T.radiusSm, padding:'0.625rem 0.875rem',
                    background: m.status==='completed' ? T.bg : T.surface,
                    opacity: m.status==='cancelled'?0.65:1,
                  }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                                  gap:'0.5rem', marginBottom:'0.35rem' }}>
                      <span style={{ fontSize:'0.8rem', fontWeight:700, color:T.text,
                                     overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {m.milestone_number && <span style={{ fontFamily:'monospace', fontSize:'0.67rem',
                          color:T.textMute, marginRight:5 }}>{m.milestone_number}</span>}
                        {m.name}
                      </span>
                      <Pill label={msLabel[m.status]||m.status} palette={mp} size="xs" />
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.625rem', marginBottom:'0.3rem' }}>
                      <div style={{ flex:1 }}>
                        <ProgressBar value={m.completion_pct} max={100} color={mp.t} height={4}/>
                      </div>
                      <span style={{ fontSize:'0.67rem', fontWeight:800, color:mp.t, minWidth:28, textAlign:'right' }}>
                        {m.completion_pct}%
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:'0.875rem', alignItems:'center' }}>
                      {m.due_date && (
                        <span style={{ fontSize:'0.65rem', color:m.is_overdue?PALETTE.red.t:T.textMute,
                                       display:'flex', alignItems:'center', gap:3 }}>
                          <Calendar size={9} strokeWidth={2}/>
                          {m.is_overdue && <span style={{ fontWeight:700 }}>Overdue · </span>}
                          {fmtShort(m.due_date)}
                        </span>
                      )}
                      {m.payment_percentage!=null && (
                        <span style={{ fontSize:'0.65rem', color:PALETTE.green.t, fontWeight:700,
                                       display:'flex', alignItems:'center', gap:3 }}>
                          <DollarSign size={9} strokeWidth={2}/>₹ {m.payment_percentage}%
                        </span>
                      )}
                      <span style={{ fontSize:'0.63rem', color:T.textMute, marginLeft:'auto' }}>
                        {m.completed_issues}/{m.total_issues}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ═══════════ TEAM & WORK HOURS ═══════════ */}
      <Card>
        <SecHead icon={Users} label="Team Workload & Hours"
          right={`${members.length} members · ${fmt1(wh?.total_hours)}h this month`} />
        {loading ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'0.75rem' }}>
            {[1,2,3,4].map(i=><Skeleton key={i} h={70}/>)}
          </div>
        ) : members.length===0 ? (
          <p style={{ fontSize:'0.78rem', color:T.textMute, margin:0 }}>No team members assigned</p>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'0.75rem' }}>
            {members.map(m => {
              const name   = m.employee_name||m.name||'—';
              const whData = wh?.by_employee?.find(e=>e.name===name);
              const hrs    = whData?.hours || 0;
              const util   = Math.min(Math.round((hrs/160)*100),100);
              const utilColor = util>90?PALETTE.red.t:util>70?PALETTE.amber.t:PALETTE.green.t;
              const rm     = { project_manager:'PM', scrum_master:'SM', tech_lead:'TL', team_lead:'Lead', member:'Member' };
              return (
                <div key={m.id} style={{
                  display:'flex', alignItems:'center', gap:'0.75rem',
                  padding:'0.75rem 0.875rem', borderRadius:T.radiusSm,
                  border:`1px solid ${T.borderSm}`, background:T.bg,
                  transition:'border-color 0.2s',
                }}
                onMouseEnter={e=>e.currentTarget.style.borderColor=T.border}
                onMouseLeave={e=>e.currentTarget.style.borderColor=T.borderSm}>
                  <Avatar name={name} photo={m.profile_photo} size={40}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.375rem', marginBottom:'0.2rem' }}>
                      <span style={{ fontSize:'0.78rem', fontWeight:700, color:T.text,
                                     overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {name}
                      </span>
                      {m.role && (
                        <span style={{ fontSize:'0.6rem', fontWeight:700, color:T.textMute,
                                       background:T.borderSm, padding:'1px 5px', borderRadius:4,
                                       flexShrink:0, border:`1px solid ${T.border}` }}>
                          {rm[m.role]||m.role}
                        </span>
                      )}
                    </div>
                    {m.emp_code && (
                      <div style={{ fontSize:'0.63rem', color:T.textMute, fontFamily:'monospace', marginBottom:'0.3rem' }}>
                        {m.emp_code}{m.department?` · ${m.department}`:''}
                      </div>
                    )}
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      <div style={{ flex:1 }}>
                        <ProgressBar value={util} max={100} color={utilColor} height={4}/>
                      </div>
                      <span style={{ fontSize:'0.65rem', fontWeight:700, color:utilColor, minWidth:32, textAlign:'right' }}>
                        {fmt1(hrs)}h
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ═══════════ PROJECT FINANCIALS ═══════════ */}
      {canSeeFinancials && (project.total_value || project.timeline_notes) && (
        <Card>
          <SecHead icon={DollarSign} label="Project Financials & Timeline" />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'1rem 1.5rem' }}>
            {[
              { icon:DollarSign,  label:'Project Value',    value: project.total_value?`₹ ${Number(project.total_value).toLocaleString('en-IN')}`:'—' },
              { icon:TrendingUp,  label:'Payment Received', value: project.payment_percentage!=null?`${project.payment_percentage}%`:'—' },
              { icon:Calendar,    label:'Start Date',       value: fmtDate(project.start_date) },
              { icon:Calendar,    label:'End Date',         value: fmtDate(project.end_date)   },
            ].map(f=>(
              <div key={f.label} style={{ display:'flex', gap:'0.5rem', alignItems:'flex-start' }}>
                <f.icon size={14} color={T.textMute} strokeWidth={1.75} style={{ marginTop:2, flexShrink:0 }}/>
                <div>
                  <div style={{ fontSize:'0.65rem', fontWeight:700, color:T.textMute,
                                textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:2 }}>
                    {f.label}
                  </div>
                  <div style={{ fontSize:'0.85rem', fontWeight:700, color:T.text }}>{f.value}</div>
                </div>
              </div>
            ))}
          </div>
          {project.timeline_notes && (
            <div style={{ marginTop:'1rem', paddingTop:'0.875rem', borderTop:`1px solid ${T.borderSm}` }}>
              <div style={{ fontSize:'0.65rem', fontWeight:700, color:T.textMute,
                            textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.375rem' }}>
                Timeline Notes
              </div>
              <p style={{ margin:0, fontSize:'0.82rem', color:T.textSub, lineHeight:1.65 }}>
                {project.timeline_notes}
              </p>
            </div>
          )}
        </Card>
      )}

    </div>
  );
}
